import type { DateRange } from 'react-day-picker'

import {
  addDays,
  addHours,
  areIntervalsOverlapping,
  endOfDay,
  endOfWeek,
  isWithinInterval,
  startOfDay,
  startOfWeek,
} from 'date-fns'

// ── TimeWindow ───────────────────────────────────────────────────────

export type TimeWindow =
  | 'all'
  | 'nextHour'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'custom'

const VALID_TIME_WINDOWS = new Set<string>([
  'all',
  'nextHour',
  'today',
  'tomorrow',
  'thisWeek',
  'custom',
])

export const isTimeWindow = (value: string): value is TimeWindow =>
  VALID_TIME_WINDOWS.has(value)

export type TimeInterval = { start: Date; end: Date }

export const getIntervalForWindow = (
  timeWindow: TimeWindow,
  referenceDate: Date,
  customDateRange: DateRange | undefined,
): TimeInterval | null => {
  switch (timeWindow) {
    case 'nextHour':
      return { start: referenceDate, end: addHours(referenceDate, 1) }
    case 'today':
      return { start: startOfDay(referenceDate), end: endOfDay(referenceDate) }
    case 'tomorrow': {
      const tomorrow = addDays(referenceDate, 1)
      return { start: startOfDay(tomorrow), end: endOfDay(tomorrow) }
    }
    case 'thisWeek':
      return {
        start: startOfWeek(referenceDate),
        end: endOfWeek(referenceDate),
      }
    case 'custom':
      if (!customDateRange?.from) return null
      // Honor the exact date+time picked in the simplified picker: the start
      // keeps its time-of-day (no startOfDay coercion) so the separately-chosen
      // time actually narrows the window. End defaults to end-of-day when no
      // explicit upper bound is set.
      return {
        start: customDateRange.from,
        end: customDateRange.to ?? endOfDay(customDateRange.from),
      }
    default:
      return null
  }
}

// ── Time window matchers ─────────────────────────────────────────────

type WorkflowTimeRange = {
  startTime: Date
  endTime: Date
}

export const isWorkflowInTimeWindow = (
  workflow: WorkflowTimeRange,
  timeWindows: TimeWindow[],
  customDateRange: DateRange | undefined,
): boolean => {
  if (timeWindows.length === 0 || timeWindows.includes('all')) {
    return true
  }

  const now = new Date()

  return timeWindows.some((timeWindow) => {
    const interval = getIntervalForWindow(timeWindow, now, customDateRange)
    return (
      interval !== null &&
      areIntervalsOverlapping(
        { start: workflow.startTime, end: workflow.endTime },
        interval,
      )
    )
  })
}

/**
 * Compute `stepsActiveAfter` / `stepsActiveBefore` API variables from
 * selected time-window filter values.  Returns `undefined` for each
 * field when no window should be applied.
 */
export const computeTimeWindowVariables = (
  selectedTimeWindows: string[],
  customDateRange: DateRange | undefined,
): {
  stepsActiveAfter: string | undefined
  stepsActiveBefore: string | undefined
} => {
  if (selectedTimeWindows.length === 0 || selectedTimeWindows.includes('all')) {
    return { stepsActiveAfter: undefined, stepsActiveBefore: undefined }
  }

  const now = new Date()
  const intervals = selectedTimeWindows
    .filter(isTimeWindow)
    .map((tw) => getIntervalForWindow(tw, now, customDateRange))
    .filter((i): i is TimeInterval => i !== null)

  if (intervals.length === 0) {
    return { stepsActiveAfter: undefined, stepsActiveBefore: undefined }
  }

  const earliest = new Date(
    Math.min(...intervals.map((i) => i.start.getTime())),
  )
  const latest = new Date(Math.max(...intervals.map((i) => i.end.getTime())))

  return {
    stepsActiveAfter: earliest.toISOString(),
    stepsActiveBefore: latest.toISOString(),
  }
}

export const isTimestampInTimeWindow = (
  timestamp: Date,
  timeWindows: TimeWindow[],
  customDateRange: DateRange | undefined,
): boolean => {
  if (timeWindows.length === 0 || timeWindows.includes('all')) {
    return true
  }

  const now = new Date()

  return timeWindows.some((timeWindow) => {
    const interval = getIntervalForWindow(timeWindow, now, customDateRange)
    return interval !== null && isWithinInterval(timestamp, interval)
  })
}
